/* eslint-disable no-restricted-imports */
import { Box, Grid, Skeleton, SxProps } from '@mui/material';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import React, { useRef, useState } from 'react';
import { useIsMobile } from '@/data/hooks/useIsMobile';
import { useWindowSize } from 'integration/hooks/useWindowSize';
import { DpH2 } from '../DPText';
import { MarksSliderProvider } from '@/data/context/content';
import dynamic from 'next/dynamic';

const Slider = dynamic(() => import('react-slick'), {
	ssr: false,
	loading: () => <Skeleton variant="rectangular" width="100vw" height={163} />,
});

type Props = {
	children: JSX.Element[];
	framesx?: SxProps;
	title?: string;
};

const baseSettings = {
	dots: false,
	infinite: true,
	slidesToScroll: 1,
	autoplay: true,
	autoplaySpeed: 4000,
	className: 'center',
	centerMode: true,
	arrows: false,
	swipeToSlide: true,
	focusOnSelect: true,
};

export const DpMarksSlider: React.FC<Props> = ({ children, framesx, title }) => {
	const isMobile = useIsMobile();
	const [width] = useWindowSize();
	const itemWidth = isMobile ? 92 : 134;
	const itemSpacing = isMobile ? 50 : 30;

	const slidesToShow = Math.floor((width + itemSpacing) / (itemWidth + itemSpacing));

	const settings = {
		...baseSettings,
		slidesToShow,
	};

	const cleanChildren = React.Children.toArray(children).filter((child) =>
		React.isValidElement(child)
	);

	const [_, setActiveSlide] = useState(0);
	const sliderRef = useRef<typeof Slider>(null);
	const [mouseMoved, setMouseMoved] = useState(false);

	return (
		<MarksSliderProvider value={{ mouseMoved }}>
			<Box
				width="100vw"
				minWidth="100%"
				maxWidth="100%"
				marginBottom={{ xs: 'var(--spacing-8)', sm: 'var(--spacing-13)' }}
				padding={'35px 0px 35px 0px'}
				sx={framesx}
			>
				{title ? <DpH2 sx={{ textAlign: 'center', marginBottom: '22px' }}>{title}</DpH2> : null}
				<Slider
					{...settings}
					{...{ ref: sliderRef }}
					beforeChange={(_, next) => {
						setActiveSlide(next);
						setMouseMoved(true);
					}}
					afterChange={() => {
						setMouseMoved(false);
					}}
				>
					{[...cleanChildren, ...cleanChildren, ...cleanChildren, ...cleanChildren].map(
						(mark, index: number) => (
							<Grid key={index}>{mark}</Grid>
						)
					)}
				</Slider>
			</Box>
		</MarksSliderProvider>
	);
};
